import { useAuth } from 'hooks/auth'  
import { useQuery } from '@tanstack/react-query'
import { AdminPermissionClaims, PermissionClaims } from '@freetech/models/user';

export const useAdminPermissions = () => {
  const { userInfo } = useAuth()

  const { data: permissions, isLoading: isPermissionsLoading } = useQuery<PermissionClaims | null>({
    queryKey: ['permissions', userInfo?.id],
    queryFn: async () => {
      if (!userInfo || userInfo.role !== 'admin') {
        return null
      }


      return userInfo.permissions || null
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userInfo && userInfo.role === 'admin',
  })


  const isAdminPermissions = (perms: PermissionClaims | null): perms is AdminPermissionClaims => {
    if (!perms) return false;
    return 'home' in perms || 'timetracker' in perms || 'user_management' in perms;
  }


  const adminPermissions = isAdminPermissions(permissions || null) ? (permissions as AdminPermissionClaims) : null;

  const checkPermission = (category: keyof AdminPermissionClaims, action: string, expectedValue?: string | boolean) => {
    if (!adminPermissions) return false;
    

    const categoryPermissions = adminPermissions[category];
    if (!categoryPermissions) return false;
    
    const permissionValue = categoryPermissions[action as keyof typeof categoryPermissions];
    

    if (expectedValue !== undefined) {
      return permissionValue === expectedValue;
    }
    

    if (typeof permissionValue === 'boolean') {
      return permissionValue === true;
    } else if (typeof permissionValue === 'string') {

      return permissionValue !== 'none' && permissionValue !== '';
    } else if (Array.isArray(permissionValue)) {

      return true;
    }
    
    return false;
  }


  const hasClientAccess = (clientId: string): boolean => {
    if (!adminPermissions || !clientId) return false;
    

    if (!checkPermission('clients', 'view')) {
      return false;
    }
    

    const clientRestrictions = adminPermissions.clients?.clientIdRestrictions;
    

    if (!Array.isArray(clientRestrictions) || clientRestrictions.length === 0) {
      return true;
    }
    

    if (clientRestrictions.length === 0) {
      return false;
    }
    

    return clientRestrictions.includes(clientId);
  }


  const getAccessibleClientIds = (): string[] | null => {
    if (!adminPermissions) return null;
    

    if (!checkPermission('clients', 'view')) {
      return [];
    }
    

    const clientRestrictions = adminPermissions.clients?.clientIdRestrictions;
    

    if (!Array.isArray(clientRestrictions)) {
      return null; // null means no restrictions - access to all
    }
    

    return clientRestrictions;
  }


  const canPerformClientAction = (action: 'view' | 'create' | 'modify' | 'delete' | 'invoicing', clientId?: string): boolean => {
    if (!adminPermissions) return false;
    

    if (action === 'view') {
      if (!checkPermission('clients', 'view')) {
        return false;
      }
      

      if (clientId) {
        return hasClientAccess(clientId);
      }
      
      return true;
    }
    

    if (!checkPermission('clients', action)) {
      return false;
    }
    

    if (action === 'create') {
      return true;
    }
    

    if (clientId) {
      return hasClientAccess(clientId);
    }
    
    return true;
  }


  const hasFreelancerAccess = (freelancerId: string): boolean => {
    if (!adminPermissions || !freelancerId) return false;
    

    if (!checkPermission('freelancers', 'view')) {
      return false;
    }
    

    const freelancerRestrictions = adminPermissions.freelancers?.freelancerIdRestrictions;
    

    if (!Array.isArray(freelancerRestrictions)) {
      return true;
    }
    

    if (freelancerRestrictions.length === 0) {
      return false;
    }
    

    return freelancerRestrictions.includes(freelancerId);
  }


  const getAccessibleFreelancerIds = (): string[] | null => {
    if (!adminPermissions) return null;
    

    if (!checkPermission('freelancers', 'view')) {
      return [];
    }
    

    const freelancerRestrictions = adminPermissions.freelancers?.freelancerIdRestrictions;
    

    if (!Array.isArray(freelancerRestrictions)) {
      return null; // null means no restrictions - access to all
    }
    

    return freelancerRestrictions;
  }


  const canPerformFreelancerAction = (action: 'view' | 'create' | 'modify' | 'delete' | 'payroll', freelancerId?: string): boolean => {
    if (!adminPermissions) return false;
    

    if (action === 'view') {
      if (!checkPermission('freelancers', 'view')) {
        return false;
      }
      

      if (freelancerId) {
        return hasFreelancerAccess(freelancerId);
      }
      
      return true;
    }
    

    if (!checkPermission('freelancers', action)) {
      return false;
    }
    

    if (action === 'create') {
      return true;
    }
    

    if (freelancerId) {
      return hasFreelancerAccess(freelancerId);
    }
    
    return true;
  }

  return { 
    permissions: adminPermissions, 
    checkPermission, 
    isPermissionsLoading,

    hasClientAccess,
    getAccessibleClientIds,
    canPerformClientAction,

    hasFreelancerAccess,
    getAccessibleFreelancerIds,
    canPerformFreelancerAction
  }
}
